import React, { useEffect, useState } from 'react';
import ThumbnailData from '../models/ThumbnailData';
import FileViewer from './FileViewer';
import { cleanFilename } from '../utils/filenameUtils';
import ErrorMessage from './ErrorMessage';
import { DOMAIN_STRINGS } from '../constants/domainStrings';
import { ERRORS } from '../constants/errorMessages';
import { Box, Button, Container, Skeleton, Stack, Tab, Tabs } from '@mui/material';
import { selectIconFromMimeType } from '../utils/iconSelector';
import PrintIcon from '@mui/icons-material/Print';
import { TEXTS } from '../constants/texts';

interface InvoiceViewerProps {
  token: string;
  invoiceId?: string;
}

const InvoiceViewer: React.FC<InvoiceViewerProps> = ({ token, invoiceId }) => {
  const [error, setError] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<ThumbnailData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeThumbnail, setActiveThumbnail] = useState<ThumbnailData | null>(null)
  const [printError, setPrintError] = useState<string | null>(null);

  const handleThumbnailClick = (event: React.SyntheticEvent, newValue: string) => {
    setActiveThumbnail(thumbnails.find(t => t.id === newValue) ?? null);
  };


  useEffect(() => {
    if (!activeThumbnail && thumbnails.length > 0) {
      setActiveThumbnail(thumbnails[0]);
    }
  }, [thumbnails, activeThumbnail]);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const apiURL = process.env.REACT_APP_API_URL as string;
        const response = await fetch(apiURL + invoiceId, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorMessage = await response.text();
          throw new Error(errorMessage + ' (' + response.status + ')');
        }

        const data = await response.json();

        if (!data || data.length < 1) {
          throw new Error(ERRORS.invoiceDataMissing);
        }

        readInvoiceData(data);
      } catch (err: any) {
        console.error(ERRORS.fetchInvoice + ":", err);
        setError(err.message || ERRORS.fetchInvoice);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceId, token]);

  const readInvoiceData = (invoiceFiles: any[]) => {

    const processedData: ThumbnailData[] = invoiceFiles.map(file => {
      const id = file.id;
      const title = cleanFilename(file.fileName);
      const mimeType = file.mimeType;
      const isInvoice = file.docType === DOMAIN_STRINGS.docTypeInvoice;
      const isConfidential = file.docType === DOMAIN_STRINGS.docTypeConfidentialAttachment ||
        file.docType === DOMAIN_STRINGS.docTypeConfidentialInvoice;
      const byteCharacters = atob(file.fileContent);
      const byteArray = new Uint8Array(
        Array.from(byteCharacters, char => char.charCodeAt(0))
      );

      const blob = new Blob([byteArray], { type: mimeType });
      const url = URL.createObjectURL(blob);

      return new ThumbnailData(id, url, title, mimeType, isInvoice, isConfidential);
    });

    setThumbnails(processedData);
  };

  const handlePrint = () => {

    const iframe = document.getElementById('file-viewer') as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } else {
      const error = TEXTS.printError;
      setPrintError(error);
    }
  };

  if (error) return <ErrorMessage message={error} />;

  return (
    <Container maxWidth="lg" sx={{ marginTop: 2 }}>
      {loading || !activeThumbnail ?
        <>
          <Stack direction="row" sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }} marginBottom={1}>
            <Box>
              <Stack direction="row">
                <Skeleton variant="rectangular" width={120} height={80} />
                <Skeleton variant="rectangular" width={120} height={80} />
                <Skeleton variant="rectangular" width={120} height={80} />
              </Stack>
            </Box>
            <Box>
              <Skeleton variant="rectangular" width={80} height={60} />

            </Box>
          </Stack>

          <Skeleton variant="rectangular" height={650} />
        </>
        :
        <>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 1
            }}
          >

            <Tabs
              value={activeThumbnail?.id}
              onChange={handleThumbnailClick}
              aria-label="Navigera mellan dokument"
              variant="scrollable"
              scrollButtons>

              {thumbnails.map(thumbnail => (
                <Tab
                  key={thumbnail.id}
                  value={thumbnail.id}
                  icon={selectIconFromMimeType(thumbnail.mimeType, 'large')}
                  aria-label={thumbnail.title}
                  label={thumbnail.title}
                />
              ))}

            </Tabs>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
              {TEXTS.print}
            </Button>
          </Stack>
          <FileViewer thumbnailData={activeThumbnail} printError={printError} />
        </>
      }
    </Container >
  );
};

export default InvoiceViewer;